import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { AppRoute, ButtonType, PlanType } from '../../types/enums';
import Button from '../Button/Button';
import Popup from '../Popup/Popup';
import PricingCard from './Card';
import Feature from './Feature';
import { PlanInfo } from '../../types/types';
import ButtonWithLoading from './Buttons';
import { fetchStripeUrl, switchPlan } from '../../requests/requests';
import useErrorHandler from '../../hooks/useErrorHandler';

import styles from './Table.module.scss';

const PricingTable = ({ loading, currentPlan }: Partial<PlanInfo> & { loading: boolean }) => {
  const errorHandler = useErrorHandler();
  const [selectedPlanType, setSelectedPlanType] = useState<PlanType>(PlanType.FREE);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [wipPopupOpen, setWipPopupOpen] = useState(false);
  const [isUpgradeInProgress, setIsUpgradeInProgress] = useState(false);

  const onSelect = (selected: PlanType, actionType?: PaymentLinkType) => {
    if (process.env.REACT_APP_DISABLE_PAYMENTS) {
      setWipPopupOpen(true);
      return;
    }

    if (actionType === PaymentLinkType.SWITCH) {
      setIsUpgradeInProgress(true);
      switchPlan(selected).then(() => {
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }, errorHandler);

      return;
    }

    setSelectedPlanType(selected);
    setIsPopupOpen(true);
  };

  const onGoToCheckout = () => {
    fetchStripeUrl(selectedPlanType).then((response) => {
      const url = response.data?.url;

      if (url) {
        window.location.href = url;
      }
    }, errorHandler);
  };

  return (
    <div>
      <div className={styles.table}>
        {plans({
          onSelect,
          loading,
          hasSubscription: currentPlan !== PlanType.FREE,
          currentPlan
        }).map((plan) => (
          <PricingCard key={plan.name} {...plan} />
        ))}
      </div>
      <Popup
        title="We are working on it!"
        onHide={() => setIsUpgradeInProgress(false)}
        show={isUpgradeInProgress}>
        Your plan is being upgraded. This may take a few seconds.
      </Popup>
      <Popup
        title="We are working on it!"
        onHide={() => setWipPopupOpen(false)}
        show={wipPopupOpen}>
        <div>
          <div style={{ marginBottom: '1rem' }}>
            Premium plans are not available at the moment, but we will notify you as soon as they
            are. If your current quota is not enough, please contact us for a custom plan.
          </div>
          <NavLink to={AppRoute.CONTACT_US}>
            <Button type={ButtonType.SECONDARY}>Contact us</Button>
          </NavLink>
        </div>
      </Popup>
      <Popup onHide={() => setIsPopupOpen(false)} title="Almost there!" show={isPopupOpen}>
        <div>
          <p>
            In order to manage your subscription, we will redirect you to Stripe, the secure payment
            processor.
          </p>
          <div className={styles.buttons}>
            <Button type={ButtonType.GHOST} action={() => setIsPopupOpen(false)}>
              Cancel
            </Button>
            <Button type={ButtonType.PRIMARY} action={onGoToCheckout}>
              Continue
            </Button>
          </div>
        </div>
      </Popup>
    </div>
  );
};

enum PaymentLinkType {
  MANAGE = 'manage',
  START = 'start',
  SWITCH = 'switch'
}

const getFreePlanButtonProps = (hasSubscription: boolean) => {
  if (hasSubscription) {
    return { text: 'Switch', link: PaymentLinkType.SWITCH };
  }
  return { text: 'Your current plan' };
};

const getPremiumSmallPlanButtonProps = (currentPlan: PlanType, hasSubscription: boolean) => {
  if (currentPlan === PlanType.PREMIUM_SMALL) {
    return { text: 'Manage subscription', link: PaymentLinkType.MANAGE };
  }
  if (hasSubscription) {
    return { text: 'Switch', link: PaymentLinkType.SWITCH };
  }
  return { text: 'Start premium', link: PaymentLinkType.START };
};

const getPremiumLargePlanButtonProps = (currentPlan: PlanType, hasSubscription: boolean) => {
  if (currentPlan === PlanType.PREMIUM_LARGE) {
    return { text: 'Manage subscription', link: PaymentLinkType.MANAGE };
  }
  if (hasSubscription) {
    return { text: 'Switch', link: PaymentLinkType.SWITCH };
  }
  return { text: 'Start premium', link: PaymentLinkType.START };
};

const getButtonProps = (currentPlan: PlanType, hasSubscription: boolean) => ({
  [PlanType.FREE]: getFreePlanButtonProps(hasSubscription),
  [PlanType.PREMIUM_SMALL]: getPremiumSmallPlanButtonProps(currentPlan, hasSubscription),
  [PlanType.PREMIUM_LARGE]: getPremiumLargePlanButtonProps(currentPlan, hasSubscription)
});

const plans = ({
  onSelect,
  hasSubscription,
  loading,
  currentPlan = PlanType.FREE
}: {
  onSelect: (selected: PlanType, link?: PaymentLinkType) => void;
  hasSubscription: boolean;
  loading: boolean;
  currentPlan?: PlanType;
}) => {
  const buttonProps = getButtonProps(currentPlan, hasSubscription);
  return [
    {
      name: 'Free forever',
      description: 'The description of the plan goes here, to show the features and the pricing.',
      price: 0,
      features: (
        <div>
          <Feature>
            Up to <span className="color-orange">2</span> boards
          </Feature>
          <Feature>
            Up to <span className="color-orange">10</span> websites
          </Feature>
          <Feature>World-class tech support</Feature>
          <Feature>Unlimited features available</Feature>
        </div>
      ),
      upgradeButton: (
        <ButtonWithLoading
          action={() => onSelect(PlanType.FREE, buttonProps[PlanType.FREE].link)}
          loading={loading}
          disabled={!hasSubscription}
          type={ButtonType.PRIMARY_INVERSE}>
          {buttonProps[PlanType.FREE].text}
        </ButtonWithLoading>
      ),
      highlighted: ''
    },
    {
      name: 'Premium small',
      description: 'The description of the plan goes here, to show the features and the pricing.',
      price: 15,
      features: (
        <div>
          <Feature>
            Up to <span className="color-orange">10</span> boards
          </Feature>
          <Feature>
            Up to <span className="color-orange">100</span> websites
          </Feature>
          <Feature>World-class tech support</Feature>
          <Feature>Unlimited features available</Feature>
        </div>
      ),
      upgradeButton: (
        <ButtonWithLoading
          loading={loading}
          action={() => onSelect(PlanType.PREMIUM_SMALL, buttonProps[PlanType.PREMIUM_SMALL].link)}
          type={ButtonType.PRIMARY}>
          {buttonProps[PlanType.PREMIUM_SMALL].text}
        </ButtonWithLoading>
      ),
      highlighted: hasSubscription
        ? currentPlan === PlanType.PREMIUM_SMALL
          ? 'Your plan'
          : ''
        : 'Developers choice'
    },
    {
      name: 'Premium large',
      description: 'The description of the plan goes here, to show the features and the pricing.',
      price: 100,
      features: (
        <div>
          <Feature>
            <span className="color-orange">Unlimited</span> boards
          </Feature>
          <Feature>
            Up to <span className="color-orange">1000</span> websites
          </Feature>
          <Feature>World-class tech support</Feature>
          <Feature>Unlimited features available</Feature>
        </div>
      ),
      upgradeButton: (
        <ButtonWithLoading
          loading={loading}
          action={() => onSelect(PlanType.PREMIUM_LARGE, buttonProps[PlanType.PREMIUM_LARGE].link)}
          type={ButtonType.PRIMARY}>
          {buttonProps[PlanType.PREMIUM_LARGE].text}
        </ButtonWithLoading>
      ),
      highlighted: currentPlan === PlanType.PREMIUM_LARGE ? 'Your plan' : ''
    }
  ];
};

export default PricingTable;
